export const ASSESSMENT_TAB = 'Avaliação';
export const QUANTITATIVE_TAB = 'Quantitativa';
export const EPI_TAB = 'EPI(s)';
export const EPC_TAB = 'EPC(s)';
export const MEASURES_TAB = 'Medidas de Controle';
export const CA_TAB = 'CA(s)';
export const ADDITIONAL_TAB = 'Dados Adicionais';
export const SUB_TABS = [QUANTITATIVE_TAB, EPI_TAB, EPC_TAB, MEASURES_TAB, CA_TAB, ADDITIONAL_TAB];
export const TABS = [ASSESSMENT_TAB, ...SUB_TABS];
export const TAB_SIZES: Record<string, number> = {
	[QUANTITATIVE_TAB]: 11,
	[EPI_TAB]: 2,
	[EPC_TAB]: 2,
	[MEASURES_TAB]: 2,
	[CA_TAB]: 1,
	[ADDITIONAL_TAB]: 1
};
export const SECTOR = 0;
export const POSITION = 3;
export const RISK = 4;
export const MATRIX = 5;
export const SELECT_WIDTH = 104;
export const SOURCE_LINE_WIDTH = 64;
export const LINE_SELECT_WIDTH = 72;
export const ASSESSMENT_SIZE = 37;
export const LINE_HEIGHT = 41;
export const NEW_LINE_HEIGHT = 33;

export type ColumnKind = 'identity' | 'text' | 'domain' | 'suggest' | 'link';
export type LinkKind = 'EPI' | 'EPC' | 'MEASURE' | 'CA' | 'SOURCE';

export interface RiskColumn {
	id: string;
	tab: string;
	index: number;
	label: string;
	kind: ColumnKind;
	width: number;
	link?: LinkKind;
	multiple?: boolean;
	fixedOptions?: string[];
	domainKey?: string;
}

export const MEASUREMENT_UNITS = ['°C', 'Lux', 'Dose Projetada [%]', 'dB (A)', 'm/s2', 'm/s² - (aren)', 'm/s1.75 (VDVR)', 'ppm',
	'mg/m3 (R)', 'mg/m³'];
export const PROPAGATION_MEANS = ['Ar - Cutâneo', 'Ar - Respiratório', 'Ar - Sonora', 'Contato', 'Não Aplicável', 'Via cutânea'];

function column(tab: string, index: number, label: string, kind: ColumnKind, width: number,
		extra: Partial<RiskColumn> = {}): RiskColumn {
	return { id: `${tab}:${index}`, tab, index, label, kind, width, ...extra };
}

function domain(tab: string, index: number, label: string, width: number, domainKey = String(index)): RiskColumn {
	return column(tab, index, label, 'domain', width, { domainKey });
}

function linked(tab: string, index: number, label: string, link: LinkKind, width: number): RiskColumn {
	return column(tab, index, label, 'link', width, { link });
}

function weights(first: number, label: string): RiskColumn[] {
	return [1, 2, 3, 4, 5].map(position => column(ASSESSMENT_TAB, first + position - 1,
		`Peso Critério ${position} (${label})`, 'suggest', 190, { domainKey: String(first + position - 1) }));
}

export const LEFT_COLUMNS: RiskColumn[] = [
	column(ASSESSMENT_TAB, SECTOR, 'Setor', 'identity', 150),
	column(ASSESSMENT_TAB, POSITION, 'Cargo', 'identity', 190),
	column(ASSESSMENT_TAB, RISK, 'Risco', 'identity', 240)
];

export const TAB_COLUMNS: Record<string, RiskColumn[]> = {
	[ASSESSMENT_TAB]: [
		domain(ASSESSMENT_TAB, 5, 'Matriz de Risco', 150),
		column(ASSESSMENT_TAB, 8, 'Fontes Geradoras', 'link', 260, { link: 'SOURCE', multiple: true }),
		domain(ASSESSMENT_TAB, 9, 'Tipo', 150),
		column(ASSESSMENT_TAB, 10, 'Meios de propagação', 'domain', 240, { multiple: true, fixedOptions: PROPAGATION_MEANS }),
		column(ASSESSMENT_TAB, 11, 'Situação da Avaliação', 'text', 170),
		domain(ASSESSMENT_TAB, 12, 'Tempo de Exposição - Tipo', 200),
		column(ASSESSMENT_TAB, 13, 'Tempo de Exposição - Detalhes', 'text', 200),
		column(ASSESSMENT_TAB, 14, 'Descrição', 'text', 280),
		column(ASSESSMENT_TAB, 15, 'Sugestões', 'text', 280),
		column(ASSESSMENT_TAB, 16, 'Riscos', 'text', 280),
		column(ASSESSMENT_TAB, 17, 'Observações Complementares', 'text', 240),
		domain(ASSESSMENT_TAB, 18, 'GFIP', 130),
		domain(ASSESSMENT_TAB, 19, 'FAE', 120),
		domain(ASSESSMENT_TAB, 20, 'Insalubridade', 180),
		domain(ASSESSMENT_TAB, 21, 'Periculosidade', 170),
		domain(ASSESSMENT_TAB, 22, 'Aposentadoria Especial', 180),
		column(ASSESSMENT_TAB, 23, 'Perigo', 'text', 240),
		column(ASSESSMENT_TAB, 24, 'Atividade', 'text', 240),
		...weights(25, 'Nível'),
		...weights(30, 'Classificação'),
		domain(ASSESSMENT_TAB, 35, 'Eficácia de EPI', 160),
		domain(ASSESSMENT_TAB, 36, 'Eficácia de EPC', 160)
	],
	[QUANTITATIVE_TAB]: [
		column(QUANTITATIVE_TAB, 0, 'Fonte Geradora', 'text', 200),
		column(QUANTITATIVE_TAB, 1, 'Nome do Instrumento', 'suggest', 240, { domainKey: 'INSTRUMENT' }),
		column(QUANTITATIVE_TAB, 2, 'Marca do Instrumento', 'suggest', 190, { domainKey: 'BRAND' }),
		column(QUANTITATIVE_TAB, 3, 'Modelo do Instrumento', 'suggest', 200, { domainKey: 'MODEL' }),
		column(QUANTITATIVE_TAB, 4, 'Técnica Utilizada', 'suggest', 230, { domainKey: 'TECHNIQUE' }),
		column(QUANTITATIVE_TAB, 5, 'Unidade de Medida', 'domain', 160, { fixedOptions: MEASUREMENT_UNITS }),
		column(QUANTITATIVE_TAB, 6, 'Nível de Ação', 'text', 130),
		column(QUANTITATIVE_TAB, 7, 'Limite de Tolerância', 'text', 150),
		column(QUANTITATIVE_TAB, 8, 'Intensidade/Concentração', 'text', 170),
		domain(QUANTITATIVE_TAB, 9, 'Tempo de Exposição - Tipo', 200, '12'),
		column(QUANTITATIVE_TAB, 10, 'Tempo de Exposição - Detalhes', 'text', 200)
	],
	[EPI_TAB]: [linked(EPI_TAB, 0, 'Recomendado(s)', 'EPI', 380), linked(EPI_TAB, 1, 'Utilizado(s)', 'EPI', 380)],
	[EPC_TAB]: [linked(EPC_TAB, 0, 'Recomendado(s)', 'EPC', 380), linked(EPC_TAB, 1, 'Utilizado(s)', 'EPC', 380)],
	[MEASURES_TAB]: [
		linked(MEASURES_TAB, 0, 'Recomendado(s)', 'MEASURE', 440),
		linked(MEASURES_TAB, 1, 'Utilizado(s)', 'MEASURE', 440)
	],
	[CA_TAB]: [linked(CA_TAB, 0, 'Utilizado(s) [Código]', 'CA', 480)],
	[ADDITIONAL_TAB]: [column(ADDITIONAL_TAB, 0, 'Ação(ões)', 'text', 520)]
};

export const ALL_COLUMNS: RiskColumn[] = [...LEFT_COLUMNS, ...TABS.flatMap(tab => TAB_COLUMNS[tab])];

export const COLUMN_BY_ID: Record<string, RiskColumn> = Object.fromEntries(ALL_COLUMNS.map(item => [item.id, item]));

export function leftWidth(): number {
	return SELECT_WIDTH + SOURCE_LINE_WIDTH + LEFT_COLUMNS.reduce((total, item) => total + item.width, 0);
}

export function tabWidth(tab: string): number {
	return (tab === ASSESSMENT_TAB ? 0 : LINE_SELECT_WIDTH)
		+ TAB_COLUMNS[tab].reduce((total, item) => total + item.width, 0);
}
